/**
 * `<FormCacheSelector>` — root component for the FormCacheSelector library.
 *
 * In this story (S015) the component adds:
 *   - A Delete icon button visible only when a cache is active (FR-FFCS-018).
 *     Desktop: standalone Trash2 button styled with `text-red-600`.
 *     Mobile: entry inside the overflow `DropdownMenu` next to Rename — also
 *     styled with `text-red-600` so the destructive intent is consistent
 *     across breakpoints (R-003).
 *   - The `DeleteDialog` wired into the hook's `deleteDialogOpen` /
 *     `deletingCacheName` / `handleDelete` API. The hook's `openDeleteDialog`
 *     helper is what the buttons invoke — it snapshots the active cache name
 *     before opening so the dialog title is frozen for the lifetime of the
 *     confirmation (R-001).
 *
 * Earlier stories established:
 *   - Developer-facing assertions for `cacheId` and `filter` (FR-FFCS-019).
 *   - Loading skeleton render branch while GetAllByUser is in-flight.
 *   - Wiring to `useFormCacheSelector` so the mount-time auto-restore flow
 *     fires `onSelected` exactly once.
 *   - Save button + SaveDialog (S013).
 *   - Rename icon button (desktop) + DropdownMenu entry (mobile) + RenameDialog (S014).
 */

import { useImperativeHandle, type Ref } from 'react';
import { Loader2, Pencil, Save, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Button } from '@components/Button/Button';
import { toast } from '@components/Toast';
import { useFormCacheSelector } from './FormCacheSelector.hooks';
import { CacheSelector } from './subcomponents/CacheSelector';
import { SaveDialog } from './subcomponents/SaveDialog';
import { RenameDialog } from './subcomponents/RenameDialog';
import { DeleteDialog } from './subcomponents/DeleteDialog';
import type { FormCacheSelectorHandle, FormCacheSelectorProps } from './FormCacheSelector.types';

export function FormCacheSelector<TFilter>(
  props: FormCacheSelectorProps<TFilter> & { ref?: Ref<FormCacheSelectorHandle> },
) {
  const { ref, cacheId, filter, onSelected, httpClient } = props;
  const { t } = useTranslation('form-cache-selector');

  // ── Developer assertions (FR-FFCS-019) ───────────────────────────────────
  // These are contract violations by the consuming developer — surface them
  // loudly via console.error + throw so they fail builds in CI and crash
  // visibly in development. They are NOT user-facing errors.
  if (!cacheId || cacheId.length === 0) {
    const message = '[FormCacheSelector] cacheId must be a non-empty string';
    console.error(message);
    throw new Error(message);
  }
  if (filter === null || filter === undefined) {
    const message = '[FormCacheSelector] filter must not be null or undefined';
    console.error(message);
    throw new Error(message);
  }

  const {
    caches,
    activeId,
    isLoading,
    isMutating,
    saveDialogOpen,
    setSaveDialogOpen,
    saveNameError,
    clearSaveNameError,
    renameDialogOpen,
    setRenameDialogOpen,
    renameError,
    clearRenameError,
    deleteDialogOpen,
    setDeleteDialogOpen,
    deletingCacheName,
    openDeleteDialog,
    activeToast,
    handleSelectCache,
    handleSelectNoFilter,
    handleUpsert,
    handleRename,
    handleDelete,
    setActiveId,
  } = useFormCacheSelector<TFilter>(cacheId, filter, onSelected, httpClient);

  useImperativeHandle(ref, () => ({ clearSelection: () => setActiveId(null) }), [setActiveId]);

  // ── Loading skeleton (UX §5.2 State 1) ───────────────────────────────────
  if (isLoading) {
    return (
      <div
        data-testid="form-cache-skeleton"
        className="animate-pulse h-8 w-full rounded bg-gray-200"
        role="status"
        aria-label={t('selector.loadingAriaLabel')}
      />
    );
  }

  /**
   * Save button click coordinator (FR-FFCS-015 / FR-FFCS-016).
   *
   * - When a cache is active: overwrite path (silent, no dialog). We look up
   *   the cache name from the local list and call `handleUpsert` directly.
   *   D-UX-007: the overwrite is non-destructive (user is overwriting their
   *   own state), so no confirmation is required.
   * - When no cache is active: open the SaveDialog so the user can supply a
   *   name. The dialog itself calls `handleUpsert` with the user's input.
   */
  const handleSaveClick = () => {
    if (activeId !== null) {
      const activeCache = caches.find((c) => c.id === activeId);
      // If the active id has somehow drifted from the local list (race with a
      // refetch), fall back to opening the dialog instead of sending an empty
      // name to the server.
      if (activeCache) {
        void handleUpsert(activeCache.name);
        return;
      }
    }
    setSaveDialogOpen(true);
  };

  // ── Active cache lookup for rename pre-fill ──────────────────────────────
  // The trigger label and rename dialog both read from this. When `activeId`
  // is null the rename affordances are hidden, so we fall back to '' safely.
  const activeCache =
    activeId !== null ? caches.find((c) => c.id === activeId) : undefined;
  const activeName = activeCache?.name ?? '';

  const openRenameDialog = () => setRenameDialogOpen(true);

  // ── Main render — dropdown selector + action buttons + dialogs ───────────
  // i18n wiring lands in a follow-up integration story; for now we ship the
  // canonical Spanish copy hardcoded at the boundary so the component is
  // useful end-to-end.
  return (
    <div data-testid="form-cache-selector" className="flex flex-col gap-1.5">
      {/* ── Button row: selector + save + rename/delete/more ────────────── */}
      <div className="flex items-center gap-1.5">
        <div className="flex-1 min-w-0">
          <CacheSelector
            caches={caches}
            activeId={activeId}
            onSelectCache={handleSelectCache}
            onSelectNoFilter={handleSelectNoFilter}
            noFilterLabel={t('selector.noFilter')}
          />
        </div>
        <Button
          htmlType="button"
          iconOnly
          type="outline"
          size="sm"
          leftIcon={isMutating ? <Loader2 className="h-4 w-4 animate-spin" data-testid="save-spinner" /> : <Save className="h-4 w-4" data-testid="save-icon" />}
          disabled={isMutating}
          ariaLabel={t('selector.saveAriaLabel')}
          onClick={handleSaveClick}
          data-testid="form-cache-save-btn"
        />
        {activeId !== null && (
          <>
            <Button
              htmlType="button"
              iconOnly
              type="outline"
              size="sm"
              leftIcon={<Pencil className="h-4 w-4" data-testid="pencil-icon" />}
              disabled={isMutating}
              ariaLabel={t('selector.renameAriaLabel')}
              onClick={openRenameDialog}
              data-testid="form-cache-rename-btn"
            />
            <Button
              htmlType="button"
              iconOnly
              type="outline"
              size="sm"
              className="text-red-600 hover:text-red-600"
              leftIcon={<Trash2 className="h-4 w-4" data-testid="trash-icon" />}
              disabled={isMutating}
              ariaLabel={t('selector.deleteAriaLabel')}
              onClick={openDeleteDialog}
              data-testid="form-cache-delete-btn"
            />
          </>
        )}
      </div>
      <SaveDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        onSubmit={handleUpsert}
        isMutating={isMutating}
        serverError={saveNameError}
        onServerErrorClear={clearSaveNameError}
      />
      <RenameDialog
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        currentName={activeName}
        onSubmit={handleRename}
        isMutating={isMutating}
        serverError={renameError}
        onServerErrorClear={clearRenameError}
      />
      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        cacheName={deletingCacheName}
        onConfirm={handleDelete}
        isMutating={isMutating}
      />
      {activeToast && (() => {
        const { text, color } = activeToast;
        const toastType = color === 'red' ? 'error' : color === 'green' ? 'success' : 'info';

        setTimeout(() => {
          if (toastType === 'error') {
            toast.error(text);
          } else if (toastType === 'success') {
            toast.success(text);
          } else {
            toast(text);
          }
        }, 0);

        return null;
      })()
      }
    </div>
  );
}
